import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { trackEvent } from "@/lib/track";
import { CreditCard, ArrowLeft, ShieldCheck } from "lucide-react";
import { money, FREE_SHIP_THRESHOLD } from "@/data/products";
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#52525B]">{label}</span>
    <input {...props} className="mt-1 w-full bg-transparent border border-zinc-700 px-3 py-2.5 font-mono text-sm outline-none focus:border-[#D4AF37] transition-colors" />
  </label>
);

export default function Checkout() {
  const navigate = useNavigate();
  const { lines, total, subtotal, bulkSavings, promoDiscount, promo, shipping, freeShip, freeShipRemaining, checkoutStripe } = useCart();
  const [f, setF] = useState({ name: "", email: localStorage.getItem("mx_email") || "", phone: "", line1: "", line2: "", city: "", state: "", zip: "" });
  const [busy, setBusy] = useState(false);
  const [agree, setAgree] = useState(false);
  const set = (k) => (e) => setF((d) => ({ ...d, [k]: e.target.value }));

  useEffect(() => {
    if (lines.length) trackEvent("checkout_started", { items: lines.length });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validate = (needEmail = true) => {
    if (!lines.length) { toast.error("Your cart is empty"); return false; }
    if (!f.name.trim()) { toast.error("Enter the recipient name"); return false; }
    if (needEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.email.trim())) { toast.error("Enter a valid email (no spaces)"); return false; }
    if (!f.phone.trim()) { toast.error("Enter a phone number"); return false; }
    if (!f.line1.trim() || !f.city.trim() || !f.state || !f.zip.trim()) { toast.error("Complete the mailing address"); return false; }
    if (!agree) { toast.error("Please accept the Terms & Research-Use-Only policy"); return false; }
    return true;
  };

  const payCard = async () => {
    if (!validate(true)) return;
    setBusy(true);
    const res = await checkoutStripe(f, agree);
    if (!res.ok) setBusy(false);
  };

  if (!lines.length) {
    return (
      <main className="pt-40 pb-24 text-center" data-testid="checkout-empty">
        <p className="font-serif text-4xl font-light mb-6">Your cart is empty.</p>
        <Link to="/" className="font-mono text-xs uppercase tracking-[0.2em] text-[#D4AF37]">← Back to catalog</Link>
      </main>
    );
  }

  return (
    <main className="pt-28 pb-24" data-testid="checkout-page">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-[#D4AF37] transition-colors mb-10" data-testid="checkout-back">
          <ArrowLeft size={15} /> Back
        </button>
        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-12 lg:gap-20">
          {/* Shipping form */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Checkout</p>
            <h1 className="font-serif text-4xl md:text-5xl font-light leading-none mb-8">Shipping details</h1>
            <div className="grid sm:grid-cols-2 gap-4" data-testid="checkout-form">
              <div className="sm:col-span-2"><Field label="Full name" value={f.name} onChange={set("name")} data-testid="checkout-name" placeholder="Recipient name" /></div>
              <Field label="Email" type="email" value={f.email} onChange={set("email")} data-testid="checkout-email" placeholder="you@lab.com" />
              <Field label="Phone" value={f.phone} onChange={set("phone")} data-testid="checkout-phone" placeholder="(555) 123-4567" />
              <div className="sm:col-span-2"><Field label="Address line 1" value={f.line1} onChange={set("line1")} data-testid="checkout-line1" placeholder="Street address" /></div>
              <div className="sm:col-span-2"><Field label="Address line 2 (optional)" value={f.line2} onChange={set("line2")} data-testid="checkout-line2" placeholder="Apt, suite, unit" /></div>
              <Field label="City" value={f.city} onChange={set("city")} data-testid="checkout-city" placeholder="City" />
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#52525B]">State</span>
                <select value={f.state} onChange={set("state")} data-testid="checkout-state" className="mt-1 w-full bg-[#0C0C0C] border border-zinc-700 px-3 py-2.5 font-mono text-sm outline-none focus:border-[#D4AF37] transition-colors">
                  <option value="">State</option>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <Field label="ZIP" value={f.zip} onChange={set("zip")} data-testid="checkout-zip" placeholder="ZIP code" />
            </div>
            <p className="mt-4 font-mono text-[10px] text-[#52525B] uppercase tracking-wide">Domestic (US) shipping only · Free on orders ${FREE_SHIP_THRESHOLD}+</p>
          </motion.div>

          {/* Summary + payment */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
            <div className="border border-zinc-800 bg-[#0C0C0C] p-7 lg:sticky lg:top-28">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#52525B] mb-5">Order summary</p>
              <ul className="space-y-2.5 mb-5 border-b border-zinc-900 pb-5">
                {lines.map((l) => (
                  <li key={l.id} className="flex justify-between text-sm gap-3">
                    <span className="text-[#A1A1AA] truncate">{l.name} <span className="text-[#52525B]">×{l.qty}</span></span>
                    <span className="font-mono text-[#F5F5F0] shrink-0">${l.lineTotal}</span>
                  </li>
                ))}
              </ul>
              {bulkSavings > 0 && <div className="flex justify-between text-sm mb-1.5"><span className="font-mono text-emerald-400 text-xs uppercase tracking-wide">Bulk savings</span><span className="font-mono text-emerald-400">-${bulkSavings}</span></div>}
              {promoDiscount > 0 && <div className="flex justify-between text-sm mb-1.5"><span className="font-mono text-emerald-400 text-xs uppercase tracking-wide">Code {promo?.code}</span><span className="font-mono text-emerald-400">-${promoDiscount}</span></div>}
              <div className="flex justify-between text-sm mb-1.5"><span className="font-mono text-[#52525B] text-xs uppercase tracking-wide">Shipping</span><span className={`font-mono ${shipping > 0 ? "text-[#E8E8E3]" : "text-emerald-400"}`} data-testid="checkout-shipping">{shipping > 0 ? money(shipping) : "Free"}</span></div>
              {!freeShip && <p className="font-mono text-[10px] text-[#D4AF37] mb-1.5" data-testid="checkout-freeship-nudge">Add {money(freeShipRemaining)} more for free shipping.</p>}
              <div className="flex justify-between text-sm mb-1.5"><span className="font-mono text-[#52525B] text-xs uppercase tracking-wide">Sales tax</span><span className="font-mono text-[#71717A]">Calculated at payment</span></div>
              <div className="flex justify-between items-baseline mt-4 mb-6">
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA]">Total</span>
                <span className="font-serif text-4xl" data-testid="checkout-total">{money(total)}</span>
              </div>
              <div className="border border-[#1E1E24] bg-[#0A0A0C] p-4 mb-4" data-testid="checkout-attestations-card">
                <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#D4AF37]/80 mb-2.5">Required attestations</p>
                <ul className="space-y-1.5 font-mono text-[10px] leading-relaxed text-[#A1A1AA]">
                  <li className="flex gap-2"><span className="text-[#D4AF37]">•</span><span>I am of legal age (21+) and eligible to purchase research materials.</span></li>
                  <li className="flex gap-2"><span className="text-[#D4AF37]">•</span><span>Research Use Only — not for human or animal consumption.</span></li>
                  <li className="flex gap-2"><span className="text-[#D4AF37]">•</span><span>No medical, dosing, or administration advice is provided or implied.</span></li>
                  <li className="flex gap-2"><span className="text-[#D4AF37]">•</span><span>I agree to the{" "}
                    <Link to="/policy/terms" target="_blank" className="text-[#D4AF37] hover:underline">Terms</Link>,{" "}
                    <Link to="/policy/research-use" target="_blank" className="text-[#D4AF37] hover:underline">RUO Policy</Link> &amp;{" "}
                    <Link to="/policy/privacy" target="_blank" className="text-[#D4AF37] hover:underline">Privacy Policy</Link>.</span></li>
                </ul>
              </div>
              <label className={`flex items-center gap-3 mb-5 cursor-pointer group border p-3 min-h-[48px] transition-colors ${agree ? "border-[#D4AF37]/60 bg-[#D4AF37]/[0.06]" : "border-[#1E1E24]"}`} data-testid="checkout-consent-label">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="checkout-consent-checkbox"
                  className="h-5 w-5 shrink-0 accent-[#D4AF37] cursor-pointer" />
                <span className={`font-mono text-[10px] leading-relaxed ${agree ? "text-[#F4F4F5]" : "text-[#A1A1AA]"}`}>
                  I am 21+ years of age, purchasing strictly for Research Use Only (RUO), and agree to all legal attestations, Terms, and Policies above.
                </span>
              </label>
              <button onClick={payCard} disabled={busy || !agree} data-testid="pay-card-btn"
                className="w-full min-h-[48px] flex items-center justify-center gap-3 bg-[#D4AF37] text-black py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#C5A028] transition-colors disabled:opacity-40 mb-3">
                <CreditCard size={17} /> {busy ? "Starting…" : "Pay with Card"}
              </button>
              <p className="flex items-center justify-center gap-2 text-[#52525B] text-[10px] font-mono text-center mt-4"><ShieldCheck size={12} /> Secure card checkout by Stripe · Questions? Use the WhatsApp button for support</p>
            </div>
          </motion.div>
        </div>
      </div>
    </main>
  );
}
